import { useMemo } from 'react';
/**
 * Sentinel / paired-page decisions for organic YMAL vs sponsored pagination.
 * Sponsored failures must not block organic loading or hold organic behind pairing.
 */

import {
    MAXIMUM_YMAL_ITEMS_COUNT,
    SOLD_PDP_YMAL_SL_INITIAL,
    SOLD_PDP_YMAL_SL_SUBSEQUENT,
    MORE_ML_ITEMS_FETCH_COUNT,
    ML_ITEMS_INITIAL_COUNT,
} from './pdpRespSimilarItemsConstants.ts';

export function canLoadPdpSimilarItemsOrganicPage({
    hasNext,
    isLoadingNext,
    hasLoadError,
    organicItemsCount,
    maxOrganicItems = MAXIMUM_YMAL_ITEMS_COUNT,
}: {
    hasNext: boolean;
    isLoadingNext: boolean;
    hasLoadError: boolean;
    organicItemsCount: number;
    maxOrganicItems?: number;
}): boolean {
    return hasNext && !isLoadingNext && !hasLoadError && organicItemsCount < maxOrganicItems;
}

export function canLoadPdpSimilarItemsSponsoredPage({
    isEffectiveVariant,
    hasNext,
    hasNextSponsored,
    isLoadingNextSponsored,
    hasSponsoredLoadError,
}: {
    isEffectiveVariant: boolean;
    hasNext: boolean;
    hasNextSponsored: boolean;
    isLoadingNextSponsored: boolean;
    hasSponsoredLoadError: boolean;
}): boolean {
    return (
        isEffectiveVariant &&
        hasNext &&
        hasNextSponsored &&
        !isLoadingNextSponsored &&
        !hasSponsoredLoadError
    );
}

/**
 * Gate display to paired pages while the variant is still loading SL inventory.
 * Once SL pagination is exhausted (or failed), reveal by organicPagesLoaded so
 * pre-inflated SLs still stream in SL10+ML10, then SL15+ML15 steps instead of dumping all at once.
 */
function getVisiblePdpSimilarItems<TOrganic, TSponsored>({
    organicItems,
    sponsoredItems,
    organicPagesLoaded,
    sponsoredPagesLoaded,
    isEffectiveVariant,
    hasSponsoredLoadError,
    isLoadingNextSponsored,
    hasNext,
    hasNextSponsored,
}: {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
    organicPagesLoaded: number;
    sponsoredPagesLoaded: number;
    isEffectiveVariant: boolean;
    hasSponsoredLoadError: boolean;
    isLoadingNextSponsored: boolean;
    hasNext: boolean;
    hasNextSponsored: boolean;
}): {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
} {
    if (!isEffectiveVariant) {
        return { organicItems, sponsoredItems };
    }

    const isWaitingForSponsoredPage =
        !hasSponsoredLoadError && (isLoadingNextSponsored || (hasNext && hasNextSponsored));

    const completedPages = isWaitingForSponsoredPage
        ? Math.min(organicPagesLoaded, sponsoredPagesLoaded)
        : organicPagesLoaded;

    return {
        organicItems: organicItems.slice(
            0,
            Math.min(
                MAXIMUM_YMAL_ITEMS_COUNT,
                ML_ITEMS_INITIAL_COUNT + completedPages * MORE_ML_ITEMS_FETCH_COUNT
            )
        ),
        sponsoredItems: sponsoredItems.slice(
            0,
            SOLD_PDP_YMAL_SL_INITIAL + completedPages * SOLD_PDP_YMAL_SL_SUBSEQUENT
        ),
    };
}

export function useVisiblePdpSimilarItems<TOrganic, TSponsored>(args: {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
    organicPagesLoaded: number;
    sponsoredPagesLoaded: number;
    isEffectiveVariant: boolean;
    hasSponsoredLoadError: boolean;
    isLoadingNextSponsored: boolean;
    hasNext: boolean;
    hasNextSponsored: boolean;
}): {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
} {
    return useMemo(() => getVisiblePdpSimilarItems(args), [args]);
}
